import {
  CaretLeft,
  CaretRight,
  MagnifyingGlass,
  Question,
  SidebarSimple,
  Stack,
} from '@phosphor-icons/react';
import { addDays } from '@shh/shared';
import { Button, IconButton, SegmentedControl } from '@shh/ui';
import { isoWeek } from '../../data/calendar';
import { useFamily } from '../../data/family';
import { calendarText } from '../../i18n/calendar';
import { dayTitleShort, monthTitle, rangeText } from './format';
import type { CalView } from './useCalendarView';
import { VIEWS } from './useCalendarView';

const VIEW_LABEL: Record<CalView, string> = {
  tag: calendarText.viewDay,
  woche: calendarText.viewWeek,
  monat: calendarText.viewMonth,
  jahr: calendarText.viewYear,
};

/**
 * The calendar's own bar under the app header (1.24): what is shown, ‹ Heute ›, the four views
 * and three small helpers (search, layers, help). The app header above keeps title, ＋ and the
 * person chip – the same on every page.
 */
export function CalendarToolbar({
  view,
  date,
  weekOf,
  onView,
  onStep,
  onToday,
  onSearch,
  onLayers,
  onHelp,
  searchOpen,
  sidebar,
  onSidebar,
}: {
  view: CalView;
  date: string;
  weekOf: string;
  onView: (v: CalView) => void;
  onStep: (dir: 1 | -1) => void;
  onToday: () => void;
  onSearch: () => void;
  onLayers: () => void;
  onHelp: () => void;
  searchOpen: boolean;
  /** Tablets and computers: the sidebar is open (it has its own search field then). */
  sidebar?: boolean;
  onSidebar?: () => void;
}) {
  const f = useFamily();
  const [title, sub] =
    view === 'tag'
      ? [dayTitleShort(date), calendarText.week(isoWeek(date))]
      : view === 'woche'
        ? [
            calendarText.week(isoWeek(weekOf)),
            `${calendarText.whoWhereWhen} · ${rangeText(weekOf, addDays(weekOf, 6))}`,
          ]
        : view === 'monat'
          ? [monthTitle(date), calendarText.week(isoWeek(date))]
          : [date.slice(0, 4), calendarText.yearHint.split('.')[0]!];
  const isToday = date === f.today;

  return (
    // one row on tablets and computers – the height is short there (Mike, 19.10.)
    <div className="flex flex-col gap-gap-sm lg:flex-row lg:items-center lg:gap-gap-md">
      <div className="flex items-center gap-gap-sm lg:contents">
        <div className="min-w-0 flex-1 lg:order-1" aria-live="polite">
          <div className="truncate font-display text-lg font-black leading-tight sm:text-xl">
            {title}
          </div>
          <div className="truncate text-sm font-semibold text-text-secondary">{sub}</div>
        </div>
        <div className="flex items-center gap-gap-xs lg:order-3">
          <IconButton
            icon={CaretLeft}
            iconWeight="bold"
            aria-label={calendarText.back}
            onPress={() => onStep(-1)}
          />
          <Button variant={isToday ? 'secondary' : 'primary'} onPress={onToday}>
            {calendarText.today}
          </Button>
          <IconButton
            icon={CaretRight}
            iconWeight="bold"
            aria-label={calendarText.forward}
            onPress={() => onStep(1)}
          />
        </div>
      </div>
      <div className="flex items-center gap-gap-xs lg:order-2">
        {onSidebar && (
          // folds in and out, remembered per device (Mike, 19.10.)
          <IconButton
            icon={SidebarSimple}
            aria-label={calendarText.sidebar}
            aria-pressed={sidebar}
            onPress={onSidebar}
          />
        )}
        <SegmentedControl
          label={calendarText.views}
          value={searchOpen ? null : view}
          onChange={onView}
          options={VIEWS.map((v) => ({ value: v, label: VIEW_LABEL[v] }))}
          className="min-w-0 flex-1 sm:flex-none"
        />
        {!sidebar && (
          <IconButton
            icon={MagnifyingGlass}
            aria-label={calendarText.search}
            aria-pressed={searchOpen}
            onPress={onSearch}
          />
        )}
        <IconButton icon={Stack} aria-label={calendarText.layers} onPress={onLayers} />
        {/* phones reach the help from the layers sheet – the row has no room */}
        <IconButton
          icon={Question}
          aria-label={calendarText.help}
          onPress={onHelp}
          className="max-sm:hidden"
        />
      </div>
    </div>
  );
}
